import { Gamepad2, Handshake, Inbox, MapPin, MapPinPlus, Megaphone, Server, Settings, Sparkles, Truck, Users, Wallet, type LucideIcon } from 'lucide-react';
import type { NavItem, NavLeaf, NavParent, NavSection, SuperAdminSectionId } from './nav';

/**
 * The Super Admin's menu (Warren, 21 October 2026, item M: "my Super Admin menu: Today inbox with counts; Network, Sales,
 * Supply, Money, People, Marketing, Settings; Ctrl+K quick jump; every other role's menu unchanged.").
 *
 * NAV_SECTIONS stays the one source of every leaf and of who may see it. This file only REGROUPS the leaves a Super
 * Admin already sees there into Warren's sections, after the usual filter has run, so a leaf cannot appear here that
 * the filter did not admit, and no other persona's menu passes through this code at all (visibilityFilter.ts).
 *
 * Every leaf is placed by id. A leaf added to NAV_SECTIONS for super_admin later and not placed here still shows, at the
 * end of Settings, so no page is ever lost; superAdminMenu.test.ts fails until it is placed on purpose.
 *
 * The cleanup of 28 October 2026 (Warren) adds collapsible groups inside Network and Marketing, and brings the EVI
 * shortcuts back as the last section (EVI_SHORTCUTS below).
 */
export const TODAY_LEAF: NavLeaf = {
  kind: 'leaf', id: 'superToday', label: 'Today', href: '/today', icon: Inbox, visibleFor: ['super_admin'],
};

/** A collapsible group inside a section: it becomes a NavParent, and is dropped when none of its leaves is admitted. */
export type PlacedGroup = { id: string; label: string; icon: LucideIcon; leaves: readonly string[] };
/** A leaf id, or a group of leaf ids. */
export type PlacedEntry = string | PlacedGroup;
type Placement = { id: Exclude<SuperAdminSectionId, 'today'>; label: string; icon: LucideIcon; items: readonly PlacedEntry[] };

export const SUPER_ADMIN_SECTIONS: readonly Placement[] = [
  // Warren, 28 October 2026: "Network: top level Dashboard, Sites, Chargers, Sessions, Alerts, Service calls, Tariffs,
  // Reports. Collapsible 'Drivers and access': Drivers, Fleets, RFID cards, Customer groups. Collapsible 'Grow the
  // network': Submit a location, Network submissions, Deployment commissioning." Network Chargers is now Chargers'
  // whole network scope (lib/chargerScope.ts); its row stays in the EVI shortcuts. 'rfidAssignments' and 'deployments'
  // keep their place for when they resolve (no leaf, and hidden from super_admin, today).
  { id: 'network', label: 'Network', icon: MapPin, items: [
    'dashboard', 'locations', 'chargers', 'sessions', 'alerts', 'serviceCalls', 'tariffs', 'reports',
    { id: 'driversAndAccess', label: 'Drivers and access', icon: Users, leaves: ['drivers', 'fleets', 'rfidCards', 'rfidAssignments', 'groups'] },
    { id: 'growTheNetwork', label: 'Grow the network', icon: MapPinPlus, leaves: ['submitLocation', 'networkSubmissions', 'eviCommissioning', 'deployments'] },
  ] },
  // Partner jobs, quotes, customers and leads, the partners themselves, and the Sales Team with its commissions.
  { id: 'sales', label: 'Sales', icon: Handshake, items: [
    // Sales Partners first, Partner Applications right under it (Warren, 25 October 2026: "Sales Partners (/agents) goes
    // first under EVI > Partners, above Partner Applications"), in NAV_SECTIONS' order.
    'agents', 'partnerApplications', 'salesJobs', 'salesQuotes', 'salesCustomers', 'leads', 'salesTeam', 'salesStaff', 'salesStatements', 'salesPayoutRun',
  ] },
  { id: 'supply', label: 'Supply', icon: Truck, items: ['eviQueues', 'eviProcurement', 'procurement', 'factorySettings'] },
  // Operators is the tenant billing roster (subscriptions and withholding); the Sales Partner Queue is payout exceptions.
  { id: 'money', label: 'Money', icon: Wallet, items: ['approvals', 'money', 'coupons', 'tenants', 'agentOpsQueue', 'carbon', 'siteElectricity'] },
  { id: 'people', label: 'People', icon: Users, items: ['team', 'lockedOut', 'roles', 'access'] },
  // Warren, 28 October 2026: "Marketing: top level Announcements, Newsletter, Blog, Conversations, Incentives.
  // Collapsible 'Gamification': Leaderboard, Badges, Earning rules, Friendships, Streaks, Challenges, Unlocks,
  // Anti-abuse." The Super Admin's Announcements is EVI > Marketing's eviAnnouncements (23 October 2026); the Manage
  // leaf 'announcements' is hidden from super_admin and stays listed for the other tiers' address.
  { id: 'marketing', label: 'Marketing', icon: Megaphone, items: [
    'eviAnnouncements', 'announcements', 'newsletter', 'blog', 'conversations', 'incentives',
    { id: 'gamification', label: 'Gamification', icon: Gamepad2, leaves: ['leaderboard', 'badges', 'earningRules', 'friendships', 'streaks', 'challenges', 'unlocks', 'antiAbuse'] },
  ] },
  // Connector Access (EVI > Platform, 23 October 2026) at its /settings/connectors address; Alerts (Warren, 29 Oct 2026,
  // item 5: "Admin, Settings, Alerts") at /settings/alerts.
  { id: 'settings', label: 'Settings', icon: Settings, items: ['settings', 'rfidPricing', 'autocharge', 'mcpConnections', 'alertSettings', 'audit'] },
];

/**
 * The EVI shortcuts, exactly as the Super Admin's bar showed them last, at stage e482183c, before item M replaced them
 * (Warren, 28 October 2026: "Bring back the EVI shortcuts menu exactly as it was before the menu change (same items,
 * same order, from git history), at the end of the bar, Super Admin only."). Same groups, leaves, order and labels;
 * the one change is Network Chargers, which opens the Chargers page on the whole network. EVI Queues joined the EVI
 * section later, in a merge, and was never in this menu; it stays under Supply. Every leaf here is also in Warren's
 * sections above, on purpose.
 */
export const EVI_SHORTCUTS: readonly PlacedGroup[] = [
  { id: 'eviSales', label: 'Sales', icon: Handshake, leaves: ['salesStaff', 'salesPayoutRun', 'salesTeam', 'salesStatements', 'leads'] },
  { id: 'eviPartners', label: 'Partners', icon: Handshake, leaves: [
    'agents', 'partnerApplications', 'agentOpsQueue', 'networkSubmissions', 'eviCommissioning', 'eviProcurement', 'factorySettings',
  ] },
  { id: 'eviMarketing', label: 'Marketing', icon: Megaphone, leaves: ['eviAnnouncements', 'newsletter', 'blog', 'conversations', 'incentives'] },
  { id: 'eviPlatform', label: 'Platform', icon: Server, leaves: [
    'tenants', 'chargerOversight', 'carbon', 'siteElectricity', 'rfidPricing', 'autocharge', 'mcpConnections',
  ] },
];

/**
 * Warren's own wording for the Super Admin, in his sections only; the EVI shortcuts and every other role keep the
 * labels NAV_SECTIONS gives them. "Card Management" read as RFID only under the RFID heading.
 */
const SUPER_ADMIN_LABELS: Readonly<Record<string, string>> = {
  rfidCards: 'RFID cards',
  groups: 'Customer groups',
  submitLocation: 'Submit a location',
  networkSubmissions: 'Network submissions',
  eviCommissioning: 'Deployment commissioning',
  earningRules: 'Earning rules',
};

/** Every leaf in these sections, parents walked, in order. */
export function leavesOf(sections: ReadonlyArray<Pick<NavSection, 'items'>>): NavLeaf[] {
  const out: NavLeaf[] = [];
  const walk = (items: ReadonlyArray<NavItem>) => { for (const i of items) { if (i.kind === 'parent') walk(i.children); else out.push(i); } };
  sections.forEach(s => walk(s.items));
  return out;
}

/** Every leaf id a placement names, groups walked. */
export function placedIds(items: ReadonlyArray<PlacedEntry>): string[] {
  return items.flatMap((e) => (typeof e === 'string' ? [e] : [...e.leaves]));
}

/**
 * The EVI shortcuts from the leaves the filter admitted: each leaf found by id, so the filter still gates every one,
 * with its own label; a group with no admitted leaf is dropped, and so is the section when every group is.
 */
export function eviShortcutsSection(admitted: ReadonlyArray<NavSection>): NavSection {
  const all = leavesOf(admitted);
  const items: NavParent[] = EVI_SHORTCUTS.flatMap((g) => {
    const children = g.leaves.flatMap((id) => all.filter((l) => l.id === id).slice(0, 1));
    return children.length ? [{ kind: 'parent' as const, id: g.id, label: g.label, icon: g.icon, children }] : [];
  });
  return { id: 'evi', label: 'EVI', icon: Sparkles, items };
}

/**
 * The Super Admin's menu from the leaves the filter admitted for them: Today first, as a single leaf, then Warren's seven
 * sections in his order, each leaf once (one entry per address) and his groups inside Network and Marketing, then the
 * EVI shortcuts last. Groups and sections left empty are dropped.
 */
export function regroupForSuperAdmin(admitted: ReadonlyArray<NavSection>): NavSection[] {
  const byHref = new Map<string, NavLeaf>();
  for (const leaf of leavesOf(admitted)) if (!byHref.has(leaf.href)) byHref.set(leaf.href, leaf);
  const leaves = [...byHref.values()];
  const placed = new Set<string>();
  const take = (id: string): NavLeaf[] => {
    const leaf = leaves.find(l => l.id === id && !placed.has(l.id));
    if (!leaf) return [];
    placed.add(leaf.id);
    return [SUPER_ADMIN_LABELS[leaf.id] ? { ...leaf, label: SUPER_ADMIN_LABELS[leaf.id]! } : leaf];
  };
  const sections: NavSection[] = SUPER_ADMIN_SECTIONS.map(s => ({
    id: s.id, label: s.label, icon: s.icon,
    items: s.items.flatMap((entry): NavItem[] => {
      if (typeof entry === 'string') return take(entry);
      const children = entry.leaves.flatMap(take);
      return children.length ? [{ kind: 'parent', id: entry.id, label: entry.label, icon: entry.icon, children }] : [];
    }),
  }));
  const evi = eviShortcutsSection(admitted);
  // A leaf only the shortcuts carry (Network Chargers) is placed there, not left over for Settings.
  for (const leaf of leavesOf([evi])) placed.add(leaf.id);
  sections.find(s => s.id === 'settings')!.items.push(...leaves.filter(l => !placed.has(l.id)));
  return [
    { id: 'today', label: 'Today', icon: Inbox, items: [TODAY_LEAF] },
    ...sections.filter(s => s.items.length > 0),
    ...(evi.items.length > 0 ? [evi] : []),
  ];
}
